<script setup lang="ts">
import type { OverlaySize } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiModal - Design System Modal Component
 *
 * A centered dialog that requires user attention and decision.
 *
 * WHEN TO USE MODAL (vs Drawer vs ActionSheet):
 * - Confirmations: "Are you sure you want to delete?"
 * - Critical decisions that block workflow
 * - Forms that need focused attention (login, create new item)
 * - Displaying important information that requires acknowledgment
 *
 * DON'T USE MODAL FOR:
 * - Supplementary content (use Drawer)
 * - Simple action lists on mobile (use ActionSheet)
 * - Content that user might reference while working (use Drawer)
 *
 * @example
 * <UiModal v-model="isOpen" title="Confirm Delete">
 *   <p>This action cannot be undone.</p>
 *   <template #footer>
 *     <UiButton variant="outline" @click="isOpen = false">Cancel</UiButton>
 *     <UiButton color="danger" @click="confirm">Delete</UiButton>
 *   </template>
 * </UiModal>
 */
import { computed, nextTick, onMounted, onUnmounted, ref, useId, watch } from 'vue';

export interface UiModalProps {
   /** Title displayed in the header */
   title?: string;
   /** Modal size */
   size?: OverlaySize;
   /** Whether to show the close button */
   closable?: boolean;
   /** Whether clicking the backdrop closes the modal */
   closeOnBackdrop?: boolean;
   /** Whether pressing Escape closes the modal */
   closeOnEscape?: boolean;
   /** Whether to prevent closing (e.g., during async operations) */
   persistent?: boolean;
   /** Center the modal vertically */
   centered?: boolean;
}

const props = withDefaults(defineProps<UiModalProps>(), {
   size: 'md',
   closable: true,
   closeOnBackdrop: true,
   closeOnEscape: true,
   persistent: false,
   centered: true,
});

const emit = defineEmits<{
   close: [];
}>();

const modelValue = defineModel<boolean>({ required: true });

// Refs for focus management
const panelRef = ref<HTMLDivElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;

const titleId = useId();

const sizeClasses = computed(() => {
   const sizes: Record<OverlaySize, string> = {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
   };
   return sizes[props.size];
});

function close() {
   if (!props.persistent) {
      modelValue.value = false;
      emit('close');
      // Restore focus to previously focused element
      if (previouslyFocusedElement) {
         previouslyFocusedElement.focus();
         previouslyFocusedElement = null;
      }
   }
}

function handleBackdropClick() {
   if (props.closeOnBackdrop) {
      close();
   }
}

function handleEscape(event: KeyboardEvent) {
   if (event.key === 'Escape' && props.closeOnEscape && modelValue.value) {
      close();
   }
}

// Focus trap - trap Tab key within modal
function handleTabTrap(event: KeyboardEvent) {
   if (event.key !== 'Tab' || !panelRef.value)
      return;

   const focusableElements = panelRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
   );
   const firstElement = focusableElements[0];
   const lastElement = focusableElements[focusableElements.length - 1];

   if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
   }
   else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
   }
}

// Lock body scroll and manage focus when modal is open
watch(modelValue, async (isOpen) => {
   if (isOpen) {
      // Save currently focused element
      previouslyFocusedElement = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleTabTrap);
      // Focus the modal panel after it renders
      await nextTick();
      panelRef.value?.focus();
   }
   else {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleTabTrap);
   }
});

onMounted(() => {
   document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
   document.removeEventListener('keydown', handleEscape);
   document.removeEventListener('keydown', handleTabTrap);
   document.body.style.overflow = '';
});
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="duration-200 ease-out"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-150 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0 z-50 flex overflow-y-auto p-4"
            :class="centered ? 'items-center justify-center' : 'items-start justify-center pt-16'"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? titleId : undefined"
         >
            <!-- Backdrop -->
            <div
               class="fixed inset-0 bg-black/50 backdrop-blur-sm"
               aria-hidden="true"
               @click="handleBackdropClick"
            />

            <!-- Modal panel -->
            <Transition
               enter-active-class="duration-200 ease-out"
               enter-from-class="opacity-0 scale-95"
               enter-to-class="opacity-100 scale-100"
               leave-active-class="duration-150 ease-in"
               leave-from-class="opacity-100 scale-100"
               leave-to-class="opacity-0 scale-95"
            >
               <div
                  v-if="modelValue"
                  ref="panelRef"
                  tabindex="-1"
                  class="relative w-full bg-white rounded-2xl shadow-xl outline-none"
                  :class="sizeClasses"
               >
                  <!-- Header -->
                  <div
                     v-if="title || closable"
                     class="flex items-center justify-between p-6 pb-0"
                  >
                     <h2 v-if="title" :id="titleId" class="text-xl font-semibold text-black">
                        {{ title }}
                     </h2>
                     <div v-else />
                     <button
                        v-if="closable"
                        type="button"
                        class="p-2 -m-2 rounded-lg text-neutral-dark hover:text-black hover:bg-neutral-light transition-colors"
                        aria-label="Close"
                        @click="close"
                     >
                        <Icon icon="mdi:close" class="size-5" />
                     </button>
                  </div>

                  <!-- Content -->
                  <div class="p-6">
                     <slot />
                  </div>

                  <!-- Footer -->
                  <div
                     v-if="$slots.footer"
                     class="flex items-center justify-center gap-4 p-6 pt-0"
                  >
                     <slot name="footer" />
                  </div>
               </div>
            </Transition>
         </div>
      </Transition>
   </Teleport>
</template>
